import { AnnotationButton } from "components/annotations/AnnotationButton";
import { ReactNode } from "react";
import styles from "../aic.module.css";
import { AitBorderRow } from "./AitBorderRow";
import { AitHeader } from "./AitHeader";
import { AitRowGroup } from "./AitRowGroup";
import { AitRowType, AitTablePosition } from "./functions/interface";
import { useTableContext } from "./TableContextProvider";

export interface AitTableProps {
  id: string;
  style?: React.CSSProperties;
}

/**
 * Render the full AIT table, including header, body row groups and controls.
 *
 * @param props - Component props
 * @param props.id - The table id
 * @param props.style - Optional container style
 */
export const AitTable = ({ id, style }: AitTableProps): ReactNode => {
  const { state } = useTableContext();
  const bodyData = state?.processedTableData?.bodyData;
  const showAnnotationPreview = state?.tlfViewMode.value === "ANNOTATIONS";

  // Calculate total number of columns for repositioning key
  // This ensures the preview bubble repositions when columns are added/removed
  const columnCount = bodyData?.[0]?.rows?.[0]?.cells?.length ?? 0;

  return !bodyData ? null : (
    <div
      className={styles.aitHolder}
      style={style}
    >
      <AnnotationButton
        buttonId={`${state.id}-table-annotation`}
        annotationType="Table"
        annotationLocation={{ position: AitTablePosition.table }}
        existingAnnotation={state?.originalTableData.tableAnnotation}
        hidden={state?.tlfViewMode.value !== "ANNOTATIONS"}
        showPreview={showAnnotationPreview}
        repositionKey={columnCount}
      />
      <table
        id={id}
        className={styles.aitTable}
      >
        <AitHeader />
        {bodyData.map((rowGroup, rgi) => (
          <AitRowGroup
            key={`row-group-${rgi}-${rowGroup.aitid}`}
            location={{
              tableSection: AitRowType.body,
              position: AitTablePosition.bodyRowGroup,
              rowGroup: rgi,
              rowGroupId: rowGroup.aitid,
            }}
          />
        ))}
        <tbody>
          <AitBorderRow id={`${id}-bottom-border`} />
        </tbody>
      </table>
    </div>
  );
};

AitTable.displayName = "AitTable";
